import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,

  HStack,
  Badge,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const Invoices = () => {
  const { t, currentLanguage } = useLanguage();
  const { formatCurrency } = useCurrency();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [proposalIdFilter, setProposalIdFilter] = useState('');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const queryClient = useQueryClient();

  const deleteInvoice = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/api/invoices/${id}`);
      queryClient.invalidateQueries(['invoices']);
    } catch (e) {}
    setDeletingId(null);
  };

  const { data: invoices, isLoading } = useQuery(['invoices', page, pageSize, searchTerm, statusFilter, proposalIdFilter, invoiceTypeFilter, dateFrom, dateTo], () =>
    api.get(`/api/invoices?limit=${pageSize}&offset=${page*pageSize}&q=${encodeURIComponent(searchTerm)}&status=${statusFilter||''}&proposal_id=${proposalIdFilter||''}&invoice_type=${invoiceTypeFilter||''}&date_from=${dateFrom||''}&date_to=${dateTo||''}`)
      .then(res => res.data.data.invoices)
  );
  const navigate = useNavigate();

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('invoices.title')}</Heading>
        <HStack>
          <Input placeholder={t('common.search')} value={searchTerm} onChange={(e)=>{setPage(0); setSearchTerm(e.target.value)}} maxW="250px" />
          <Select size="sm" placeholder={t('common.status')} value={statusFilter} onChange={(e)=>{setPage(0); setStatusFilter(e.target.value)}} maxW="200px">
            <option value="draft">{t('invoices.status.draft')}</option>
            <option value="sent">{t('invoices.status.sent')}</option>
            <option value="paid">{t('invoices.status.paid')}</option>
            <option value="overdue">{t('invoices.status.overdue')}</option>
          </Select>
          <Select size="sm" placeholder={t('invoices.type')||'Type'} value={invoiceTypeFilter} onChange={(e)=>{setPage(0); setInvoiceTypeFilter(e.target.value)}} maxW="180px">
            <option value="advance">Advance</option>
            <option value="completion">Completion</option>
            <option value="extra">Extra</option>
          </Select>
          <Input placeholder={t('proposals.id')||'Proposal ID'} value={proposalIdFilter} onChange={(e)=>{setPage(0); setProposalIdFilter(e.target.value)}} maxW="150px" />
          <Input type="date" value={dateFrom} onChange={(e)=>{setPage(0); setDateFrom(e.target.value)}} maxW="160px" />
          <Input type="date" value={dateTo} onChange={(e)=>{setPage(0); setDateTo(e.target.value)}} maxW="160px" />
          <Button leftIcon={<FiPlus />} colorScheme="brand" size="sm" px={7} py={1} fontSize="sm" fontWeight="medium" minW="130px">{t('invoices.new')}</Button>
        </HStack>
      </HStack>

      <Card>
        <CardBody>
          {isLoading ? (
            <Text>{t('common.loading')}</Text>
          ) : (
            <>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>{t('invoices.number')}</Th>
                    <Th>{t('invoices.customer')}</Th>
                    <Th isNumeric>{t('invoices.total')}</Th>
                    <Th>{t('invoices.status')}</Th>
                    <Th>{t('invoices.date')}</Th>
                    <Th></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invoices?.map(inv => (
                    <Tr key={inv.id} onClick={()=> navigate(`/invoices/${inv.id}`)} style={{ cursor: 'pointer' }}>
                      <Td>{inv.invoice_number}</Td>
                      <Td>{inv.client_name}</Td>
                      <Td isNumeric>{formatCurrency(inv.invoiced_amount || inv.total_amount || 0, inv.currency)}</Td>
                      <Td><Badge>{t(`invoices.status.${inv.status}`, {defaultValue: inv.status})}</Badge></Td>
                      <Td>{new Date(inv.created_at).toLocaleDateString()}</Td>
                      <Td>
                        <HStack justify="flex-end">
                          <Button size="xs" variant="outline" onClick={async () => {
                            try {
                              const res = await api.get(`/api/invoices/${inv.id}/pdf?lang=${currentLanguage}`, { responseType: 'blob' });
                              const blob = new Blob([res.data], { type: 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              setTimeout(()=> window.URL.revokeObjectURL(url), 10000);
                            } catch (e) {}
                          }}>{t('common.export_pdf', {defaultValue: 'PDF'})}</Button>
                          <Button size="xs" colorScheme="brand" variant="outline" onClick={async ()=>{
                            await api.post(`/api/invoices/${inv.id}/email`, {});
                          }}>{t('common.send_email', {defaultValue: 'Email'})}</Button>
                          <Button size="xs" colorScheme="red" variant="outline" isLoading={deletingId===inv.id} onClick={e => {e.stopPropagation(); deleteInvoice(inv.id);}} leftIcon={<FiTrash2 />}>{t('common.delete', {defaultValue: 'Sil'})}</Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <HStack justify="space-between" mt={4}>
                <HStack>
                  <Button size="sm" onClick={() => setPage(p => Math.max(0, p-1))} isDisabled={page===0}>{t('common.prev')}</Button>
                  <Button size="sm" onClick={() => setPage(p => p+1)} isDisabled={(invoices?.length||0) < pageSize}>{t('common.next')}</Button>
                </HStack>
                <HStack>
                  <Text fontSize="sm">{t('common.rows')}</Text>
                  <Select size="sm" value={pageSize} onChange={(e)=>{setPage(0); setPageSize(Number(e.target.value));}} maxW="80px">
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Select>
                </HStack>
              </HStack>
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default Invoices;


